import { QueryFiltersExecutor } from "../converters/queries/query-filter";
import { autoinject, bindable, BindingEngine } from "aurelia-framework";
import { transient } from "aurelia-dependency-injection";
import { QueriesClientService } from "../../../services/queries/queries-client.services";
import * as Enumerable from "linq";
import { Disposable } from "aurelia-binding";
import { QueryFilter } from "../../../models/queries/queries.models";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { ItemSource, SourceType } from "../../../models/queries/queries.models";
import { UserClientService } from "../../../services/authentication/user-client.service";
import { Guid } from "../../../utilities/guid";

@autoinject
@transient()
export class GridView {

    public scrollContainer: HTMLElement;
    private baseODataUrl: string;
    private completeCollection: any[];
    private _attached: boolean;
    private collection: any[];
    private enablePrevious: boolean;
    private enableNext: boolean;
    private count: number;
    private unsubscriberForCollectionChanged: Disposable;
    private itemSourceChanging: boolean = false;
    private fetchingData: boolean = false;

    @bindable
    isLoading: boolean;

    @bindable
    lastPage: number;

    @bindable
    itemsCollection: any[] = null;

    @bindable
    page: number = 0;

    @bindable
    pageCount: number = 10;

    @bindable
    itemSource: ItemSource;

    @bindable
    selectedFilters: QueryFilter[] = [];

    @bindable
    currentPage: number;

    @bindable
    disablePaging: boolean = false;

    @bindable
    additionalProperties: { [index: string]: any };

    @bindable
    onItemClicked;

    @bindable
    onCollectionChanged;

    @bindable
    identifier: string = Guid.generateGuid();

    itemsCollectionChanged(): void {
        if (this.unsubscriberForCollectionChanged) {
            this.unsubscriberForCollectionChanged.dispose();
        }
        this.unsubscriberForCollectionChanged = this.bindingEngine.collectionObserver(this.itemsCollection).subscribe((recordsChange) => {
            if (this.onCollectionChanged) {
                const recordsAdded = this.itemsCollection.slice(recordsChange[0].index, recordsChange[0].addedCount + recordsChange[0].index);
                const data = { recordsChange: { added: recordsAdded, remove: recordsChange[0].removed } };
                const hiddenItemsCount = this.onCollectionChanged(data);
                if (hiddenItemsCount > 0) {
                    this.page++;
                }
            }
        });
    }

    detached(): void {
        if (this.unsubscriberForCollectionChanged != null) {
            this.unsubscriberForCollectionChanged.dispose();
        }
    }

    itemClicked($event: Event, item: any): void {
        if (this.onItemClicked) {
            this.onItemClicked({ item: item });
        }
    }

    @bindable
    public columnConfig: string;

    itemSourceChanged(newValue: ItemSource): void {
        this.itemSourceChanging = true;
        this.completeCollection = [];
        this.itemsCollection = [];
        this.page = 0;
        if (newValue.sourceType === SourceType.Collection) {
            this.completeCollection = newValue.source;
            this.count = this.completeCollection.length;
        } else if (newValue.sourceType === SourceType.ODataUrl) {
            this.baseODataUrl = newValue.source;
        }
        if (!this._attached) { return; }
        this.reload();
        this.itemSourceChanging = false;
    }

    pageChanged(): void {
        if (!this._attached || this.itemSourceChanging) { return; }
        this.reload();
        $(this.scrollContainer).animate({ scrollTop: 0 }, 0);
    }

    /**
     * Called when next button is pressed to increase the current page by 1 and reload the grid
     */
    nextClick(): void {
        if (this.enableNext) {
            this.page++;
        }
    }

    /**
     * Called when prev button is pressed to decrease the current page by 1 and reload the grid
     */
    previousClick(): void {
        if (this.enablePrevious) {
            this.page--;
        }
    }

    selectedFiltersChanged() {
        if (!this._attached) { return; }
        this.page = 0;
        this.reload();
    }

    scrolling($event: Event): void {
        const elem = $($event.currentTarget) as any;
        if (Math.abs((<any>elem[0]).scrollHeight - elem.scrollTop() - elem.outerHeight()) <= 2) {
            if (this.currentPage > this.lastPage && this.disablePaging || this.fetchingData) { return; }
            this.page += 1;
        }
    }

    private buildGridView(collection: any[]): void {
        this.itemsCollection = [];
        for (let index = 0; index < collection.length; index++) {
            const i = index;
            const item = collection[i];
            const keys = Enumerable.from(Object.keys(item));
            for (const property in this.additionalProperties) {
                if (keys.all(x => x !== property)) {
                    item[property] = CloneHelper.clone(this.additionalProperties[property]);
                }
            }
            item["isSelected"] = false;
            item["isVisible"] = true;
            this.itemsCollection.push(item);
        }
        const gridBuiltEvent: CustomEvent = new CustomEvent("grid-built", { detail: this.itemsCollection ? this.itemsCollection.length : 0, bubbles: true });
        this.element.dispatchEvent(gridBuiltEvent);
        this.isLoading = false;
    }

    constructor(private element: Element,
        private queryService: QueriesClientService,
        private bindingEngine: BindingEngine,
        private userClientService: UserClientService) {
    }


    attached(): void {
        this._attached = true;
    }

    bind(): void {
        this.completeCollection = [];
        this.itemsCollection = [];
        if (this.itemSource) {
            if (this.itemSource.sourceType === SourceType.Collection) {
                this.completeCollection = this.itemSource.source;
                this.count = this.completeCollection.length;
            } else if (this.itemSource.sourceType === SourceType.ODataUrl) {
                this.baseODataUrl = this.itemSource.source;
            }
        }
        this.reload();
    }

    public reload(): void {
        if (!this.itemSource) { return; }
        this.isLoading = true;
        if (this.itemSource.sourceType == SourceType.ODataUrl) {
            this.buildODataUrl().then(completeODataUrl => {
                this.fetchingData = true;
                this.queryService.getData(completeODataUrl, this.identifier).then(res => {

                    this.count = res["@odata.count"] as number;

                    if (!this.count) {
                        this.count = 1;
                    }

                    const result = (res).value;
                    const current = (Number(this.pageCount) * Number((this.page + 1)));
                    this.enableNext = Number(this.count) > Number(current);
                    this.enablePrevious = Number(this.page) > 0;
                    this.currentPage = Number(this.page) + 1;
                    this.lastPage = Math.ceil(this.count / this.pageCount);

                    if (this.currentPage > this.lastPage && !this.disablePaging) {
                        this.page = 0;
                        this.reload();
                    }

                    const resultLoadedEvent: CustomEvent = new CustomEvent("result-loaded", { detail: result, bubbles: true });
                    this.element.dispatchEvent(resultLoadedEvent);

                    this.rebuildGridView(result);
                    this.fetchingData = false;
                }).catch(() => { this.fetchingData = false; });
            });
        } else if (this.itemSource.sourceType == SourceType.Collection) {
            const current = (Number(this.pageCount) * Number((this.page + 1)));
            this.enableNext = Number(this.count) > Number(current);
            this.enablePrevious = Number(this.page) > 0;
            this.currentPage = Number(this.page) + 1;
            this.lastPage = Math.ceil(this.count / this.pageCount);

            if (this.currentPage > this.lastPage && !this.disablePaging) {
                this.page = 0;
                this.reload();
            }
            const filteredCollection = this.collection.slice(this.page * this.pageCount, this.page * this.pageCount + this.pageCount);
            this.rebuildGridView(filteredCollection);
        }
    }

    private rebuildGridView(coll: any[]): void {
        if (!this.disablePaging) {
            this.collection = coll;
        } else if (this.collection) {
            this.collection.concat(coll);
        } else {
            this.collection = coll;
        }
        this.buildGridView(coll);
    }

    public lostFocus(): void {
        this.reload();
    }

    private buildODataUrl(): Promise<string> {
        const filters = this.selectedFilters ?
            (this.itemSource.initialFilters ? this.selectedFilters.concat(this.itemSource.initialFilters) : this.selectedFilters)
            : this.itemSource.initialFilters;
        const queryFiltersExecutor = new QueryFiltersExecutor(null,
            filters,
            this.page,
            this.pageCount,
            this.itemSource.initialSorting, this.userClientService.currentUser);
        return queryFiltersExecutor.getUri(false, true, this.baseODataUrl);
    }
}
